import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Building, User, Zap, ShieldCheck } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from '@/lib/supabase';

export function ModeToggle() {
  const { user, currentMode, activeLicenses, setMode } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin on mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const {
          data: { user: supabaseUser },
          error: userError,
        } = await supabase.auth.getUser();
        if (userError) {
          console.error('Error fetching user for admin check:', userError);
          return;
        }
        setIsAdmin(
          supabaseUser?.email
            ? ['jonathan@newloop.ai', 'admin@newloop.ai', 'support@newloop.ai'].includes(
                supabaseUser.email
              )
            : false
        );
      } catch (error) {
        console.error('Error in checkAdminStatus:', error);
      }
    };
    checkAdminStatus();
  }, []);

  // Memoized available modes to boost performance
  const availableModesMemo = useMemo(() => {
    const modes = [{ mode: 'standard', icon: User }]; // Always include standard

    // Add Pro if licensed
    if (activeLicenses.includes('pro')) {
      modes.push({ mode: 'pro', icon: Zap });
    }

    // Add Hire if licensed
    if (activeLicenses.includes('hire')) {
      modes.push({ mode: 'hire', icon: Building });
    }

    // Add Admin if authorized
    if (isAdmin) {
      modes.push({ mode: 'admin', icon: ShieldCheck });
    }

    return modes;
  }, [activeLicenses, isAdmin]);

  // Preserve the existing function name
  const getAvailableModes = () => {
    return availableModesMemo;
  };

  // Get current mode index
  const getCurrentIndex = () => {
    const modes = getAvailableModes();
    return Math.max(0, modes.findIndex((m) => m.mode === currentMode));
  };

  // Get next mode
  const getNextMode = () => {
    const modes = getAvailableModes();
    const currentIndex = getCurrentIndex();
    const nextIndex = (currentIndex + 1) % modes.length;
    return modes[nextIndex].mode;
  };

  const handleToggle = async () => {
    if (isLoading || isAnimating) return;

    setIsLoading(true);
    setIsAnimating(true);

    try {
      const nextMode = getNextMode();

      // If nextMode is hire, confirm user is part of a company - but don't block standard mode
      if (nextMode === 'hire' && currentMode !== 'hire') {
        try {
          const {
            data: companyUserData,
            error: companyUserError,
          } = await supabase
            .from('company_users')
            .select('company_id')
            .eq('user_id', user?.id)
            .maybeSingle();

          if (companyUserError) {
            console.error('Error fetching company user data:', companyUserError);
          }

          // If no company association found, show warning but still allow switching to standard mode
          if (!companyUserData && nextMode === 'hire') {
            toast({
              variant: 'destructive',
              title: 'Company Association Required',
              description: 'Please join or claim a company before switching to Hire Mode.',
            });
            
            // If trying to switch to hire mode but no company, don't proceed
            if (nextMode === 'hire') {
              setIsLoading(false);
              setIsAnimating(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking company association:', error);
          // Continue with mode switch even if check fails
        }
      }

      // Update local state first for instant UI feedback
      setMode(nextMode);

      // Navigate based on mode
      if (nextMode === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }

      // Update the database
      const {
        data: { user: supabaseUser },
        error: getUserError,
      } = await supabase.auth.getUser();

      if (getUserError) {
        console.error('Error fetching user during mode update:', getUserError);
        throw new Error('Could not retrieve user from Supabase.');
      }

      if (!supabaseUser) {
        throw new Error('No user found to update mode.');
      }

      const { error: insertError } = await supabase.rpc('set_user_mode', {
        p_user_id: supabaseUser.id,
        p_new_mode: nextMode,
      });

      if (insertError) {
        console.error('Supabase RPC set_user_mode failed:', insertError);
        throw new Error('Failed to update your mode in the database.');
      }
    } catch (error: any) {
      console.error('Error toggling mode:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to switch modes. Please try again.',
      });
      // Stop animation to allow re-trying
      setIsAnimating(false);
    } finally {
      setIsLoading(false);
      // Reset animation after a brief delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  // Get available modes for display
  const availableModes = getAvailableModes();

  // Get current and next icons
  const currentIndex = getCurrentIndex();
  const nextIndex = (currentIndex + 1) % availableModes.length;

  const CurrentIcon = availableModes[currentIndex].icon;
  const NextIcon = availableModes[nextIndex].icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative rounded-full w-10 h-10 overflow-hidden"
            onClick={handleToggle}
            disabled={isLoading || isAnimating || availableModes.length <= 1}
          >
            <div className="relative w-full h-full">
              {/* Current Icon */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-all duration-300 transform ${
                  isAnimating
                    ? 'translate-y-[-150%] rotate-12 opacity-0'
                    : 'translate-y-0 rotate-0 opacity-100'
                }`}
              >
                <CurrentIcon className="h-5 w-5" />
              </div>

              {/* Next Icon */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-all duration-300 transform ${
                  isAnimating
                    ? 'translate-y-0 rotate-0 opacity-100'
                    : 'translate-y-[150%] rotate-12 opacity-0'
                }`}
              >
                <NextIcon className="h-5 w-5" />
              </div>
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isLoading ? (
            "Switching mode..."
          ) : currentMode === 'admin' ? (
            "Admin Mode Active"
          ) : currentMode === 'hire' ? (
            "NewLoop Hire Active"
          ) : currentMode === 'pro' ? (
            "NewLoop Pro Active"
          ) : (
            "NewLoop Standard"
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}